import { APP_VERSION } from './appVersion';

/**
 * Recovering from a route chunk that is no longer on the server.
 *
 * Every build names its chunks by content hash and a deploy replaces `assets/`
 * wholesale, so an `index.html` a webview loaded before the deploy points at
 * files that do not exist after it. The next lazy route load then fails and the
 * router falls through to `ErrorPage` -- the "calendar's lost in time" screen
 * users report. Observed on 2026-09-16: the 14:56 deploy of `d29a761f` broke a
 * session running `94367ca0` at 15:01:15, while it was still open
 * (TELECAL-WEB-APP-14W, 12 users).
 *
 * A reload fixes it. `apps/calendar/nginx/nginx.conf` serves `index.html` with
 * `Cache-Control: no-cache, must-revalidate`, so a fresh document names the
 * chunks that are actually there. `reset()` cannot fix it: a rejected dynamic
 * import stays rejected in the browser's module map, so re-running the route
 * load asks for the same specifier and fails without making a request.
 */

/**
 * Messages that mean "the module is not there", across the engines Telegram
 * runs on.
 *
 * Deliberately specific. A bare `Failed to fetch` would also match the API
 * being briefly unreachable (TELECAL-WEB-APP-14K, 14 users in 24h), and every
 * backend deploy makes that happen -- reloading then would turn a ten-second
 * blip into a refresh storm against a server that is already restarting.
 * Everything here names a *module* load.
 */
const STALE_CHUNK_MESSAGES = [
  // Chromium, so Telegram on Android. The same wording for a 404 and for the
  // HTML below.
  'failed to fetch dynamically imported module',
  // WebKit, so Telegram on iOS -- what a 404 from `/assets/` produces
  // (TELECAL-WEB-APP-150).
  'importing a module script failed',
  // Gecko.
  'error loading dynamically imported module',
  // HTML with a 200 where the chunk should be, rejected on its MIME type.
  // nginx used to answer a missing asset with `index.html`; `/assets/` is a
  // 404 now, but an image built before that still does it -- after a
  // rollback, or on the `front-*` host a rollout has not reached yet.
  // WebKit's wording, then Chromium's (TELECAL-WEB-APP-M2 is the first).
  'is not a valid javascript mime type',
  'failed to load module script',
  // Vite's preload helper, when it is the route's stylesheet rather than its
  // chunk that has gone.
  'unable to preload css for',
];

/** How far down a `cause` chain to look before giving up. */
const MAX_CAUSE_DEPTH = 5;

/**
 * Whether this failure is a chunk the server no longer has.
 *
 * Walks `cause`, because the router and Vite's preload helper both wrap what
 * they catch, and the useful message can sit a level or two down.
 */
export function isStaleChunkError(error: unknown): boolean {
  let current = error;

  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth += 1) {
    if (typeof current !== 'object' || current === null) {
      return false;
    }

    const { cause, message } = current as Partial<{
      cause: unknown;
      message: unknown;
    }>;

    if (typeof message === 'string') {
      const haystack = message.toLowerCase();

      if (STALE_CHUNK_MESSAGES.some((needle) => haystack.includes(needle))) {
        return true;
      }
    }

    current = cause;
  }

  return false;
}

/** Where a pending reload is remembered, so it can be capped and reported. */
export const STALE_CHUNK_STORAGE_KEY = 'tgcal:staleChunkReload';

type StaleChunkMarker = {
  /** The build that could not load its chunk. */
  appVersion: string;
  at: number;
  /** Set once a failed recovery has been reported, so it is reported once. */
  reported?: boolean;
};

export type StaleChunkOutcome = {
  /** The build the reload was made from. */
  fromVersion: string;
  /** Whether the reload landed on a different build, which is the fix working. */
  recovered: boolean;
};

// `window.sessionStorage` rather than the bare global: Node ships its own
// Web Storage globals from 22.4 and they shadow the ones jsdom provides, which
// is the trap `GoogleAuthPage.spec.tsx` documents. Reads and writes are wrapped
// because the accessor itself throws in a private window or with site data
// blocked.
function readMarker(): StaleChunkMarker | null {
  try {
    const raw = window.sessionStorage.getItem(STALE_CHUNK_STORAGE_KEY);

    return raw ? (JSON.parse(raw) as StaleChunkMarker) : null;
  } catch {
    return null;
  }
}

function writeMarker(marker: StaleChunkMarker): boolean {
  try {
    window.sessionStorage.setItem(
      STALE_CHUNK_STORAGE_KEY,
      JSON.stringify(marker),
    );

    return true;
  } catch {
    return false;
  }
}

function clearMarker() {
  try {
    window.sessionStorage.removeItem(STALE_CHUNK_STORAGE_KEY);
  } catch {
    // Nothing to do: a storage that cannot be written cannot hold a marker.
  }
}

/**
 * Whether this page has already started a reload.
 *
 * Module state rather than sessionStorage on purpose: it lives exactly as long
 * as the document does, and a reload is a new document, so it cannot outlive
 * the navigation it describes. The marker above is the opposite -- it has to
 * survive the reload to cap it and report it.
 */
let reloadUnderWay = false;

/**
 * Whether this page is on its way out to a reload, so anything that fails in
 * the meantime is a symptom of the stale build and not worth showing or
 * reporting.
 *
 * It is not only a stale-chunk message that fails in the meantime. Cancelling
 * `vite:preloadError` (see `reloadOnPreloadError`) makes the route's import
 * *resolve*, with `undefined`, and TanStack's `lazyRouteComponent` then throws
 * `Cannot read properties of undefined (reading 'component')` -- a TypeError
 * that no message list recognises (TELECAL-WEB-APP-15M, 2026-09-16).
 */
export function isReloadingForStaleChunk(): boolean {
  return reloadUnderWay;
}

/**
 * Reload to pick up the current build, at most once per build.
 *
 * Returns whether a reload is under way, so the caller can stay quiet rather
 * than reporting an error it is about to navigate away from. That includes a
 * reload this page already started: a deploy usually takes every chunk a
 * navigation needs at once -- a layout and its page, a component and its
 * loader -- and each one fails on its own. The second failure finds the marker
 * the first one just wrote, and answering "no" to it let its error through to
 * `ErrorPage`, which reported it and drew the skull while the reload was
 * already in flight (TELECAL-WEB-APP-150, 2026-09-17).
 *
 * The cap is keyed on `APP_VERSION`, which is stamped into index.html at build
 * time -- the document a reload fetches fresh -- and that
 * is what makes it self-clearing: a reload that works arrives on a *different*
 * build, whose key is untouched, so the next deploy is recovered from too. A
 * reload that does not work comes back on the same build and finds its own
 * marker, so the user gets the screen and its button instead of a tab that
 * refreshes forever. That matters because a missing asset is not always a
 * deploy -- a half-rolled-out `front-1`/`front-2`, or a proxy holding a stale
 * `index.html`, would otherwise loop.
 *
 * With no working sessionStorage there is no cap, so nothing is reloaded
 * automatically; the button still works, because a person tapping it is its own
 * limit.
 *
 * A phone that lost signal mid-navigation fails the same way and reports the
 * same message, and reloading *that* replaces a screen offering a button with
 * whatever the webview shows for an unreachable page. `navigator.onLine` is
 * only worth consulting in one direction -- true means there is an interface,
 * not that the internet works -- but false is reliable, so it is worth the two
 * lines it costs.
 */
export function reloadForStaleChunk(): boolean {
  if (reloadUnderWay) {
    return true;
  }

  if (navigator.onLine === false) {
    return false;
  }

  if (readMarker()?.appVersion === APP_VERSION) {
    return false;
  }

  if (!writeMarker({ appVersion: APP_VERSION, at: Date.now() })) {
    return false;
  }

  reloadUnderWay = true;
  window.location.reload();

  return true;
}

/**
 * The `vite:preloadError` listener `app/instruments.ts` installs.
 *
 * Vite's preload helper dispatches this when a route's chunk or stylesheet
 * cannot be fetched, and rethrows the error only if nothing cancels the event.
 * A deploy takes the chunks an already-open session is pointing at, so that
 * rethrow is the usual way onto the "calendar's lost in time" screen, and
 * reloading is what recovers it.
 *
 * Cancelling only when a reload is actually under way is the point of the
 * return value: a reload the cap refused leaves the error to propagate, so the
 * user gets the screen and its button rather than a webview that quietly does
 * nothing.
 *
 * Cancelling has a side effect worth knowing: the import then resolves, with
 * `undefined`, so the router still fails -- just with a TypeError instead of
 * the real error. `ErrorPage` checks `isReloadingForStaleChunk` for exactly
 * that, and neither draws nor reports anything while the reload happens.
 */
export function reloadOnPreloadError(event: Event) {
  if (reloadForStaleChunk()) {
    event.preventDefault();
  }
}

/**
 * What became of a reload this tab made earlier, once per outcome.
 *
 * Both branches are reported, because "how often does a deploy break an open
 * session" and "how often does reloading fix it" are different questions and
 * only the second says whether this code works. A recovered marker is cleared;
 * a failed one is kept, so the cap above stays spent for this build, and
 * flagged, so it is only sent once.
 *
 * A page that is itself reloading has nothing to report yet. The marker it
 * would read is the one it has just written, for its own build, which looks
 * exactly like a reload that came back to the same broken build: until this
 * check, every such reload filed a `recovered = false` row a moment before
 * the new build filed its `recovered = true` one (2026-09-16 21:21:58 and
 * 21:22:01, the reload behind TELECAL-WEB-APP-15M). The marker is left
 * untouched for the page the reload brings back, which reports it properly.
 */
export function consumeStaleChunkReload(): StaleChunkOutcome | null {
  if (reloadUnderWay) {
    return null;
  }

  const marker = readMarker();

  if (!marker?.appVersion) {
    return null;
  }

  if (marker.appVersion !== APP_VERSION) {
    clearMarker();

    return { fromVersion: marker.appVersion, recovered: true };
  }

  if (marker.reported) {
    return null;
  }

  writeMarker({ ...marker, reported: true });

  return { fromVersion: marker.appVersion, recovered: false };
}
